import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Card,
  CardHeader,
  Text,
  Button,
  Field,
  Slider,
  Input,
  tokens,
  shorthands,
  Spinner,
} from "@fluentui/react-components";
import {
  Calculator24Regular,
  ArrowReset24Regular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  card: {
    width: "100%",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("16px"),
    ...shorthands.padding("16px"),
  },
  controlRow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("16px"),
    flexWrap: "wrap",
  },
  controlGroup: {
    flex: "1",
    minWidth: "250px",
  },
  sliderContainer: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
  },
  valueDisplay: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  buttonGroup: {
    display: "flex",
    ...shorthands.gap("8px"),
    marginTop: "8px",
  },
  helpText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
});

interface ScenarioTestingControlsProps {
  defaultInterestRate?: number; // Default interest rate (e.g., 0.15 for 15%)
  defaultShippingTime?: number; // Default shipping time in days
  defaultPaymentTerms?: number; // Default payment terms in days
  onRecalculate: (interestRate: number, shippingTime: number, paymentTerms: number) => Promise<void>;
  onResetToDefaults: () => Promise<void>; // Called when resetting to defaults (no override parameters)
  isCalculating: boolean;
}

const ScenarioTestingControls: React.FC<ScenarioTestingControlsProps> = ({
  defaultInterestRate = 0.15,
  defaultShippingTime = 90,
  defaultPaymentTerms = 30,
  onRecalculate,
  onResetToDefaults,
  isCalculating,
}) => {
  const styles = useStyles();

  const [interestRate, setInterestRate] = useState(defaultInterestRate);
  const [shippingTime, setShippingTime] = useState(defaultShippingTime);
  const [paymentTerms, setPaymentTerms] = useState(defaultPaymentTerms);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const hasInterestChange =
      Math.abs(interestRate - defaultInterestRate) > 0.0001;
    const hasShippingChange = shippingTime !== defaultShippingTime;
    const hasPaymentChange = paymentTerms !== defaultPaymentTerms;
    setHasChanges(hasInterestChange || hasShippingChange || hasPaymentChange);
  }, [interestRate, shippingTime, paymentTerms, defaultInterestRate, defaultShippingTime, defaultPaymentTerms]);

  const handleRecalculate = async () => {
    await onRecalculate(interestRate, shippingTime, paymentTerms);
  };

  const handleReset = async () => {
    setInterestRate(defaultInterestRate);
    setShippingTime(defaultShippingTime);
    setPaymentTerms(defaultPaymentTerms);
    // Immediately recalculate with defaults (no override parameters)
    await onResetToDefaults();
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <Card className={styles.card}>
      <CardHeader
        header={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Calculator24Regular />
            <Text weight="semibold">Scenario Testing</Text>
          </div>
        }
        description={
          <Text className={styles.helpText}>
            Adjust parameters to see how changes affect CFROI calculations
          </Text>
        }
      />
      <div className={styles.cardContent}>
        <div className={styles.controlRow}>
          <div className={styles.controlGroup}>
            <Field label="Interest Rate (WACC)">
              <div className={styles.sliderContainer}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text className={styles.valueDisplay}>
                    {formatPercentage(interestRate)}
                  </Text>
                  <Input
                    type="number"
                    value={(interestRate * 100).toFixed(1)}
                    onChange={(_, data) => {
                      const value = parseFloat(data.value) / 100;
                      if (!isNaN(value) && value >= 0 && value <= 1) {
                        setInterestRate(value);
                      }
                    }}
                    contentAfter="%"
                    size="small"
                    style={{ width: "100px" }}
                  />
                </div>
                <Slider
                  value={interestRate * 100}
                  onChange={(_, data) => setInterestRate(data.value / 100)}
                  min={0}
                  max={50}
                  step={0.5}
                  disabled={isCalculating}
                />
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Text size={200}>0%</Text>
                  <Text size={200}>50%</Text>
                </div>
              </div>
            </Field>
          </div>

          <div className={styles.controlGroup}>
            <Field label="Shipping Time (Days)">
              <div className={styles.sliderContainer}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text className={styles.valueDisplay}>
                    {shippingTime} days
                  </Text>
                  <Input
                    type="number"
                    value={shippingTime.toString()}
                    onChange={(_, data) => {
                      const value = parseInt(data.value);
                      if (!isNaN(value) && value >= 0 && value <= 365) {
                        setShippingTime(value);
                      }
                    }}
                    contentAfter="days"
                    size="small"
                    style={{ width: "120px" }}
                  />
                </div>
                <Slider
                  value={shippingTime}
                  onChange={(_, data) => setShippingTime(data.value)}
                  min={0}
                  max={180}
                  step={1}
                  disabled={isCalculating}
                />
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Text size={200}>0 days</Text>
                  <Text size={200}>180 days</Text>
                </div>
              </div>
            </Field>
          </div>

          <div className={styles.controlGroup}>
            <Field label="Payment Terms (Days)">
              <div className={styles.sliderContainer}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text className={styles.valueDisplay}>
                    {paymentTerms} days
                  </Text>
                  <Input
                    type="number"
                    value={paymentTerms.toString()}
                    onChange={(_, data) => {
                      const value = parseInt(data.value);
                      if (!isNaN(value) && value >= 0 && value <= 365) {
                        setPaymentTerms(value);
                      }
                    }}
                    contentAfter="days"
                    size="small"
                    style={{ width: "120px" }}
                  />
                </div>
                <Slider
                  value={paymentTerms}
                  onChange={(_, data) => setPaymentTerms(data.value)}
                  min={0}
                  max={180}
                  step={1}
                  disabled={isCalculating}
                />
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Text size={200}>0 days</Text>
                  <Text size={200}>180 days</Text>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <Button
            appearance="primary"
            icon={<Calculator24Regular />}
            onClick={handleRecalculate}
            disabled={!hasChanges || isCalculating}
          >
            {isCalculating ? (
              <>
                <Spinner size="tiny" /> Recalculating...
              </>
            ) : (
              "Recalculate CFROI"
            )}
          </Button>
          <Button
            appearance="secondary"
            icon={<ArrowReset24Regular />}
            onClick={handleReset}
            disabled={!hasChanges || isCalculating}
          >
            Reset to Defaults
          </Button>
        </div>

        {hasChanges && !isCalculating && (
          <Text
            size={200}
            style={{ color: tokens.colorPaletteYellowForeground1 }}
          >
            ⚠️ Click "Recalculate CFROI" to see results with new parameters
          </Text>
        )}
      </div>
    </Card>
  );
};

export default ScenarioTestingControls;
